/**
 * Helpers for resolving a rider's associated-user entries to a display label.
 *
 * A rider's `associatedUsers` is a JSON array of entries referencing a user by
 * email or Cognito sub. When the referenced user no longer exists (deleted, or
 * recreated under a new email) the entry is "orphaned". The UI must NOT render
 * the raw email/UUID for an unresolved entry — it shows a non-identifying
 * "Former user" label instead. (FR-4, Q3=A)
 */

/** A parsed entry from a rider's `associatedUsers` array. */
export interface AssociationEntry {
  user_id: string;
  role: string;
  email?: string;
  name?: string;
}

/** Minimal shape of a user record used to resolve an association. */
export interface AssociationUser {
  email?: string;
  username?: string;
  name?: string;
  given_name?: string;
}

/** Result of resolving an association entry to a display label. */
export interface ResolvedAssociation {
  /** True when the entry maps to a known user. */
  resolved: boolean;
  /** Display label — a user's name when resolved, else "Former user". Never a raw id. */
  label: string;
  /** The association role, preserved for context. */
  role: string;
}

/** Label shown for an association that no longer resolves to a known user. */
export const FORMER_USER_LABEL = 'Former user';

/**
 * Find the user record matching an association entry, by email or username.
 * Matches against the entry's `user_id` and its `email` field (case-insensitive).
 */
export function findAssociatedUser(
  assoc: AssociationEntry,
  users: AssociationUser[]
): AssociationUser | undefined {
  const ids = [assoc.user_id, assoc.email]
    .filter((v): v is string => typeof v === 'string' && v.trim() !== '')
    .map((v) => v.trim().toLowerCase());
  if (ids.length === 0) return undefined;

  return users.find((u) => {
    const email = u.email?.trim().toLowerCase();
    const username = u.username?.trim().toLowerCase();
    return (
      (email !== undefined && email !== '' && ids.includes(email)) ||
      (username !== undefined && username !== '' && ids.includes(username))
    );
  });
}

/**
 * Resolve an association entry to a display label.
 *
 * - Resolved → the user's name (or given_name); never falls back to a raw id.
 * - Unresolved → `{ resolved: false, label: "Former user" }` so no raw
 *   email/UUID is ever rendered. (FR-4)
 */
export function resolveAssociation(
  assoc: AssociationEntry,
  users: AssociationUser[]
): ResolvedAssociation {
  const user = findAssociatedUser(assoc, users);
  if (user) {
    const name = (user.name || user.given_name || '').trim();
    if (name) {
      return { resolved: true, label: name, role: assoc.role };
    }
  }
  return { resolved: false, label: FORMER_USER_LABEL, role: assoc.role };
}
